import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import accountsApi from '../../services/accountsApi';
import { Plus, Receipt } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_COLORS = {
  Draft: 'bg-gray-100 text-gray-600',
  Submitted: 'bg-yellow-100 text-yellow-700',
  Approved: 'bg-blue-100 text-blue-700',
  Posted: 'bg-green-100 text-green-700',
  Rejected: 'bg-red-100 text-red-700',
  Cancelled: 'bg-gray-100 text-gray-500',
  Reversed: 'bg-purple-100 text-purple-700',
};

const STATUSES = ['Draft', 'Submitted', 'Approved', 'Posted', 'Rejected', 'Cancelled', 'Reversed'];

const VoucherList = () => {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchVouchers = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const response = await accountsApi.get('/vouchers', { params });
      setVouchers(response.data.vouchers);
    } catch (err) {
      toast.error('Failed to load vouchers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchVouchers(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Layout title="Vouchers">
      <div className="flex items-center justify-between mb-6">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <Link
          to="/vouchers/new"
          className="flex items-center gap-2 bg-blue-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-900"
        >
          <Plus size={16} /> New Voucher
        </Link>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Voucher No</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Type</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Cost Center</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Purpose</th>
              <th className="text-right px-4 py-3 text-gray-600 font-semibold">Amount</th>
              <th className="text-center px-4 py-3 text-gray-600 font-semibold">Status</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Created</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-8 text-gray-500">Loading...</td></tr>
            ) : vouchers.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-gray-500">
                <Receipt size={32} className="mx-auto mb-2 text-gray-300" />
                No vouchers found
              </td></tr>
            ) : vouchers.map(v => (
              <tr key={v.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3">
                  <Link to={`/vouchers/${v.id}`} className="font-mono font-medium text-blue-700 hover:underline">
                    {v.voucher_no}
                  </Link>
                </td>
                <td className="px-4 py-3 text-gray-600">{v.voucher_type_code}</td>
                <td className="px-4 py-3 text-gray-600">{v.cost_center_code || '—'}</td>
                <td className="px-4 py-3 text-gray-700 max-w-xs truncate">{v.purpose}</td>
                <td className="px-4 py-3 text-right font-medium text-gray-800">
                  {Number(v.gross_amount || v.net_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[v.status] || 'bg-gray-100 text-gray-600'}`}>
                    {v.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-500 text-xs">{v.created_at?.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-sm text-gray-500 mt-2">Total: {vouchers.length} vouchers</div>
    </Layout>
  );
};

export default VoucherList;
